import React from 'react';
import { Dialog, Box, Typography, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import COLORS from '@/resources/style/Themes';
import { usePvdConfigEdit } from '@/hooks/pvd_config_hooks/usePvdConfigEdit';
import { VoltageSensitivitySection } from './components/VoltageSensitivitySection';
import CurrentSensitivitySection from './components/CurrentSensitivitySection';
import type { PvdConfigurationOnAzure } from '@/../generated-client/models';
import { StringKeys } from '@/resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';

interface Props {
  show: boolean;
  onHide: () => void;
  config: PvdConfigurationOnAzure;
  sensitivityType: 'voltage' | 'current';
}

export const PvdSensitivityQuickViewModal: React.FC<Props> = ({
  show,
  onHide,
  config,
  sensitivityType,
}) => {
  const { t } = useTranslation();
  const editor = usePvdConfigEdit(config);

  const voltageEnabled = editor.isVoltageEnabled();
  const currentEnabled = editor.isCurrentEnabled();

  return (
    <Dialog
      open={show}
      onClose={onHide}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            boxShadow: 3,
            '@media (max-width: 600px)': {
              transform: 'scale(0.85)',
              transformOrigin: 'center',
              margin: 2,
            },
          },
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: COLORS.SAFEGUARD_BLUE,
          color: 'white',
          px: 2,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          {config.spec_name} - {t(StringKeys.READ_ONLY)}
        </Typography>
        <IconButton onClick={onHide} size="small" sx={{ color: 'white' }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ pointerEvents: 'none', userSelect: 'none' }}>
        {sensitivityType === 'voltage' ? (
          <VoltageSensitivitySection
            editor={editor}
            voltageEnabled={voltageEnabled}
            setVoltageEnabled={() => {}}
            readonly={true}
          />
        ) : (
          <CurrentSensitivitySection
            editor={editor}
            currentEnabled={currentEnabled}
            setCurrentEnabled={() => {}}
            readonly={true}
          />
        )}
      </Box>

      <Box sx={{ p: 2, pt: 0 }}>
        <Typography variant="body2" color="text.secondary">
          *
          {config.is_base_config
            ? t(StringKeys.BASE_CONFIG_LOCKED_INFO)
            : t(StringKeys.READ_ONLY_DESCRIPTION)}
        </Typography>
      </Box>
    </Dialog>
  );
};
